import { Inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { combineLatest, Observable, of } from "rxjs";
import { map } from "rxjs/operators";

import { LOCALE } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import { isBaiduEngine } from "@hermes/map-utils";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

import {
    fetchShopsStock,
    launchProductLocator,
    resetProductLocator,
} from "./product-locator.actions";
import {
    selectHasError,
    selectProductHasNoShopsStock,
    selectPLReadyToOpen,
    selectProductLocatorLoading,
    selectShopsStock,
    selectShopIdsWithoutStock,
} from "./product-locator.selectors";
import { ProductLocatorState } from "./product-locator.state";

@Injectable()
export class ProductLocatorFacade {
    /**
     * Emit true when the Product Locator has all the necessary conditions to open.
     */
    public readyToOpen$ = this.store.select(selectPLReadyToOpen);

    /**
     * Describe the loading status of the Product Locator
     */
    public loading$ = this.store.select(selectProductLocatorLoading);

    /**
     * Emit true when there is no error but the Product Locator is not compatible with the product (no stocks or no shops).
     */
    public productHasNoShopsStock$ = this.store.select(
        selectProductHasNoShopsStock,
    );

    /**
     * Emit true when any of the information required for the opening has not been retrieved.
     */
    public hasError$ = this.store.select(selectHasError);

    /**
     * Describe the stocks by shops
     */
    public shopsStock$ = this.store.select(selectShopsStock);

    /**
     * Describe the shops (from Shop Locator) that have not stock (Product Locator)
     */
    public shopIdsWithoutStock$ = this.store.select(selectShopIdsWithoutStock);

    public displayMap$: Observable<boolean> = combineLatest([
        of(isBaiduEngine(this.locale.countryCode)),
        this.breakpointService.mediumBreakpointObserver(),
    ]).pipe(map(([isUsingBaidu, { matches }]) => !isUsingBaidu && matches));

    constructor(
        @Inject(LOCALE) private locale: Locale,
        private store: Store<ProductLocatorState>,
        private breakpointService: BreakpointService,
    ) {}

    /**
     * For a given product, launch the feature Product Locator.
     */
    public launchProductLocator(skus: string[]): void {
        this.store.dispatch(launchProductLocator({ skus }));
    }

    /**
     * For a given product, get stocks
     */
    public fetchShopsStock(skus: string[]): void {
        this.store.dispatch(fetchShopsStock({ skus }));
    }

    /**
     * Reset the Product Locator feature.
     */
    public resetProductLocator(): void {
        this.store.dispatch(resetProductLocator());
    }
}
